import { createFileRoute } from "@tanstack/react-router";
import { Clause, LegalLayout } from "@/components/sox/LegalLayout";
import { LEGAL, LEGAL_ADDRESS_LINE } from "@/lib/legal";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Sale — SOXIE365" },
      {
        name: "description",
        content:
          "The contract terms for buying the SOXIE365 365 Pack: pricing, VAT, delivery, customs, warranty and dispute resolution.",
      },
      { property: "og:title", content: "Terms of Sale — SOXIE365" },
      {
        property: "og:description",
        content: "Contract terms, consumer rights and warranties for SOXIE365 orders.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://www.soxie365.com/terms" },
    ],
    links: [{ rel: "canonical", href: "https://www.soxie365.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalLayout
      index="03"
      title="Terms of Sale"
      intro={`These terms govern every order placed on ${LEGAL.domain} with ${LEGAL.entity}. They sit alongside — and never reduce — the mandatory consumer rights you have where you live.`}
    >
      <Clause heading="1. The seller">
        <p>
          {LEGAL.entity}, {LEGAL_ADDRESS_LINE}. Company number {LEGAL.registrationNumber}, VAT{" "}
          {LEGAL.vatNumber}. Contact:{" "}
          <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>.
        </p>
      </Clause>

      <Clause heading="2. The product">
        <p>
          The 365 Pack contains 365 individually packed pairs of no-show socks in a single colour
          (all black or all white, selected by you at checkout). Product photography is
          illustrative; minor variation in shade, stitching and packaging is normal and not a
          defect. We may substitute equivalent or better materials without notice.
        </p>
      </Clause>

      <Clause heading="3. Orders and contract formation">
        <p>
          Your order is an offer to buy. A contract forms only when we send an order confirmation
          email. We may refuse or cancel an order — with a full refund — if the item is unavailable,
          the price was clearly mis-stated, payment fails or is flagged as fraudulent, or delivery to
          your location is restricted by law or by our carriers.
        </p>
      </Clause>

      <Clause heading="4. Price, VAT and duties">
        <p>
          The 365 Pack is <strong>€365.00</strong>, inclusive of applicable VAT and standard
          shipping within the EU. Prices are shown in euro; any USD figure on the site is an indicative
          conversion only and the euro amount is what is charged. Applicable tax is calculated
          automatically at checkout based on your delivery address.
        </p>
        <p>
          For deliveries outside the EU, import duties or local taxes may be charged by your
          customs authority. Where our carrier collects these as a delivered-duty-paid shipment we
          will say so at checkout; otherwise those charges are yours.
        </p>
      </Clause>

      <Clause heading="5. Payment">
        <p>
          Payments are processed by Stripe. By paying you confirm you are legally entitled to use
          the payment method. We never see or store your full card details.
        </p>
      </Clause>

      <Clause heading="6. Delivery and risk">
        <p>
          Estimated transit times are shown on the site (typically 3–5 business days in the EU,
          5–8 in North America, 7–12 elsewhere) and are estimates, not guarantees. For EU
          consumers, if we have not delivered within 30 days you may set a further reasonable
          period and, if that passes, cancel for a full refund.
        </p>
        <p>
          Risk of loss passes to you on delivery. Title passes when payment clears in full.
        </p>
      </Clause>

      <Clause heading="7. Cancellation, returns and refunds">
        <p>
          EU consumers have a statutory 14-day right of withdrawal, and we voluntarily extend
          our own return window to 30 days. Full details, conditions and the model withdrawal form
          are in our Returns &amp; Refunds policy.
        </p>
      </Clause>

      <Clause heading="8. Legal guarantee and warranty">
        <p>
          Goods must be as described, fit for purpose and of satisfactory quality. EU consumers
          benefit from the two-year legal conformity guarantee under Directive (EU) 2019/771; US
          buyers keep any implied warranties that cannot be excluded under their state's law. Normal
          wear from use, damage caused by misuse, and non-compliance with care instructions are not
          covered.
        </p>
      </Clause>

      <Clause heading="9. Liability">
        <p>
          Nothing here limits liability for death or personal injury caused by negligence, for
          fraud, or for anything else that cannot lawfully be limited — including mandatory consumer
          protections. Subject to that, our total liability for any order is limited to the amount
          you paid for it, and we are not liable for indirect or consequential loss.
        </p>
      </Clause>

      <Clause heading="10. Acceptable use and intellectual property">
        <p>
          The SOXIE365 name, logo, copy, imagery and site design are our property and may not be
          copied or used commercially without written permission. You agree not to interfere with the
          site, scrape it at scale, or resell the product as new without authorisation.
        </p>
      </Clause>

      <Clause heading="11. Governing law and disputes">
        <p>
          These terms are governed by the law of {LEGAL.governingLaw}. If you are a consumer, you
          keep the protection of the mandatory laws of your country of residence and may bring
          proceedings there. EU consumers can also use the European Commission's Online Dispute
          Resolution platform. We ask you to contact{" "}
          <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a> first — almost everything
          is solved in one email.
        </p>
      </Clause>
    </LegalLayout>
  );
}
